class PlayerAggregator {
  constructor(battingData, bowlingData) {
    this.battingData = battingData;
    this.bowlingData = bowlingData;
  }

  getPlayerPerformances(playerName, statType) {
    const data = statType === 'batting' ? this.battingData : this.bowlingData;
    return data.filter(row => row['Player Name'] === playerName);
  }

  aggregateByTeam(playerName, statType) {
    const performances = this.getPlayerPerformances(playerName, statType);
    const teamStats = {};

    performances.forEach(perf => {
      const team = perf['Team'];
      if (!teamStats[team]) {
        teamStats[team] = [];
      }
      teamStats[team].push(perf);
    });

    return Object.keys(teamStats).sort().map(team => ({
      team,
      performances: teamStats[team].sort((a, b) => 
        parseInt(b['Year']) - parseInt(a['Year'])
      )
    }));
  }

  aggregateByFormat(playerName, statType) {
    const performances = this.getPlayerPerformances(playerName, statType);
    const formatStats = {};

    performances.forEach(perf => {
      const format = perf['Format'];
      if (!formatStats[format]) {
        formatStats[format] = {
          performances: [],
          totals: {}
        };
      }
      formatStats[format].performances.push(perf);
    });

    // Calculate totals for each format
    Object.keys(formatStats).forEach(format => {
      const perfs = formatStats[format].performances;
      if (statType === 'batting') {
        formatStats[format].totals = {
          matches: perfs.reduce((sum, p) => sum + (parseInt(p['Matches']) || 0), 0),
          innings: perfs.reduce((sum, p) => sum + (parseInt(p['Innings']) || 0), 0),
          runs: perfs.reduce((sum, p) => sum + (parseInt(p['Runs']) || 0), 0),
          balls: perfs.reduce((sum, p) => sum + (parseInt(p['Balls']) || 0), 0),
          fours: perfs.reduce((sum, p) => sum + (parseInt(p['Fours']) || 0), 0),
          sixes: perfs.reduce((sum, p) => sum + (parseInt(p['Sixes']) || 0), 0),
        };
        // Calculate average and SR
        formatStats[format].totals.avg = formatStats[format].totals.innings > 0 
          ? (formatStats[format].totals.runs / formatStats[format].totals.innings).toFixed(2)
          : 0;
        formatStats[format].totals.sr = formatStats[format].totals.balls > 0 
          ? (formatStats[format].totals.runs / formatStats[format].totals.balls * 100).toFixed(2)
          : 0;
      } else {
        formatStats[format].totals = {
          matches: perfs.reduce((sum, p) => sum + (parseInt(p['Matches']) || 0), 0),
          overs: perfs.reduce((sum, p) => sum + (parseFloat(p['Overs']) || 0), 0),
          maidens: perfs.reduce((sum, p) => sum + (parseInt(p['Maidens']) || 0), 0),
          runsConceded: perfs.reduce((sum, p) => sum + (parseInt(p['Runs Conceded']) || 0), 0),
          wickets: perfs.reduce((sum, p) => sum + (parseInt(p['Wickets']) || 0), 0),
        };
        // Calculate average and economy
        formatStats[format].totals.avg = formatStats[format].totals.wickets > 0 
          ? (formatStats[format].totals.runsConceded / formatStats[format].totals.wickets).toFixed(2)
          : 'N/A';
        formatStats[format].totals.economy = formatStats[format].totals.overs > 0 
          ? (formatStats[format].totals.runsConceded / formatStats[format].totals.overs).toFixed(2)
          : 0;
      }
    });

    return formatStats;
  }

  getOverallStats(playerName, statType) {
    const performances = this.getPlayerPerformances(playerName, statType);
    
    if (performances.length === 0) {
      return null;
    }

    if (statType === 'batting') {
      return {
        matches: performances.reduce((sum, p) => sum + (parseInt(p['Matches']) || 0), 0),
        innings: performances.reduce((sum, p) => sum + (parseInt(p['Innings']) || 0), 0),
        runs: performances.reduce((sum, p) => sum + (parseInt(p['Runs']) || 0), 0),
        balls: performances.reduce((sum, p) => sum + (parseInt(p['Balls']) || 0), 0),
        fours: performances.reduce((sum, p) => sum + (parseInt(p['Fours']) || 0), 0),
        sixes: performances.reduce((sum, p) => sum + (parseInt(p['Sixes']) || 0), 0),
        avg: (performances.reduce((sum, p) => sum + (parseInt(p['Runs']) || 0), 0) / 
              performances.reduce((sum, p) => sum + (parseInt(p['Innings']) || 0), 0)).toFixed(2),
        sr: (performances.reduce((sum, p) => sum + (parseInt(p['Runs']) || 0), 0) / 
             performances.reduce((sum, p) => sum + (parseInt(p['Balls']) || 0), 0) * 100).toFixed(2)
      };
    } else {
      return {
        matches: performances.reduce((sum, p) => sum + (parseInt(p['Matches']) || 0), 0),
        overs: performances.reduce((sum, p) => sum + (parseFloat(p['Overs']) || 0), 0),
        maidens: performances.reduce((sum, p) => sum + (parseInt(p['Maidens']) || 0), 0),
        runsConceded: performances.reduce((sum, p) => sum + (parseInt(p['Runs Conceded']) || 0), 0),
        wickets: performances.reduce((sum, p) => sum + (parseInt(p['Wickets']) || 0), 0),
        avg: (performances.reduce((sum, p) => sum + (parseInt(p['Runs Conceded']) || 0), 0) / 
              performances.reduce((sum, p) => sum + (parseInt(p['Wickets']) || 0), 0)).toFixed(2),
        economy: (performances.reduce((sum, p) => sum + (parseInt(p['Runs Conceded']) || 0), 0) / 
                  performances.reduce((sum, p) => sum + (parseFloat(p['Overs']) || 0), 0)).toFixed(2)
      };
    }
  }
}

export default PlayerAggregator;
